import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { api, stationLabel } from "@/lib/api";
import { readCart } from "@/lib/cart";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Reusable "Chiama Cameriere" bell used in customer-facing pages (Home + Menu).
// - Bell icon always visible top-right.
// - If cart.station is set (via QR or explicit manual pick), confirm dialog fires waiter_call.
// - If no station, dialog explains and offers a link back to the station picker.
//   NO service request is created without an explicit station.
// - 180s anti-spam per-station, persisted in sessionStorage (per-tab).
export default function CallWaiterBell() {
  const [cart, setCart] = useState(readCart());
  const [open, setOpen] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [sending, setSending] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    const handler = () => setCart(readCart());
    window.addEventListener("sb-cart-change", handler);
    return () => window.removeEventListener("sb-cart-change", handler);
  }, []);

  useEffect(() => {
    const stationId = cart.station?.id;
    if (!stationId) { setCooldown(0); return; }
    const key = `sb_waiter_until_${stationId}`;
    const tick = () => {
      const until = parseInt(sessionStorage.getItem(key) || "0", 10);
      setCooldown(Math.max(0, Math.ceil((until - Date.now()) / 1000)));
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [cart.station]);

  const station = cart.station;
  const hasStation = !!(station && station.id && station.name);

  const handleConfirm = async () => {
    if (!hasStation || cooldown > 0 || sending) return;
    setSending(true);
    try {
      await api.post("/public/service-requests", {
        station_token: station.token || null,
        station_id: station.id,
        station_name: station.name,
        reason: "waiter_call",
      });
      const until = Date.now() + 180_000;
      sessionStorage.setItem(`sb_waiter_until_${station.id}`, String(until));
      setCooldown(180);
      setOpen(false);
      toast.success("Richiesta inviata. Un cameriere arriverà appena possibile.");
    } catch (e) {
      toast.error("Errore invio richiesta");
    } finally {
      setSending(false);
    }
  };

  const goToStationPicker = () => {
    setOpen(false);
    nav("/select-station", { state: { returnTo: loc.pathname } });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="outline"
        size="icon"
        disabled={cooldown > 0}
        onClick={() => setOpen(true)}
        data-testid="call-waiter-btn"
        aria-label="Chiama cameriere"
        className="rounded-full h-11 w-11 relative bg-white border-2 border-bronze-600 text-bronze-600 shadow-sm hover:bg-bronze-600 hover:text-white disabled:opacity-100 shrink-0"
      >
        <Bell className="w-5 h-5" strokeWidth={2} />
        {cooldown > 0 && (
          <span
            data-testid="waiter-cooldown"
            className="absolute -bottom-1 -right-1 rounded-full bg-bronze-600 text-white text-[10px] leading-none px-1.5 py-0.5 font-medium border border-white"
          >
            {cooldown}s
          </span>
        )}
      </Button>

      <DialogContent className="max-w-sm" data-testid={hasStation ? "waiter-dialog-confirm" : "waiter-dialog-no-station"}>
        {hasStation ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Chiamare un cameriere?</DialogTitle>
              <DialogDescription className="text-ink-500" data-testid="waiter-confirm-description">
                Stai richiedendo l'assistenza di un cameriere presso <b className="text-ink-900">{stationLabel(station)}</b>.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-4 flex-row justify-end gap-2 sm:justify-end">
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => setOpen(false)}
                data-testid="waiter-cancel-btn"
              >
                Annulla
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={sending}
                className="rounded-full"
                data-testid="waiter-confirm-btn"
              >
                Chiama
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Seleziona prima la postazione</DialogTitle>
              <DialogDescription className="text-ink-500">
                Per chiamare un cameriere devi indicare da quale ombrellone, tavolo, lettino o area VIP stai effettuando la richiesta.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-4 flex-row justify-end gap-2 sm:justify-end">
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => setOpen(false)}
                data-testid="waiter-cancel-btn"
              >
                Annulla
              </Button>
              <Button
                onClick={goToStationPicker}
                className="rounded-full"
                data-testid="waiter-pick-station-btn"
              >
                Seleziona postazione
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
